import React, {FormEvent, useCallback, useEffect, useRef, useState} from 'react'
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap'
import {Link} from 'react-router-dom'
import {createUseStyles} from 'react-jss'
import {faRetweet, faStop} from '@fortawesome/free-solid-svg-icons'
import {restConfig} from '../authConfig'
import Card from '../components/Card'
import CardSection from '../components/CardSection'
import Pagination from '../components/Pagination'
import {Toolbar, ToolbarItem} from '../components/Learning/Toolbar'
import {Colors} from '../constants/colors'
import useFetchClient from '../hooks/useFetchClient'
import {
  CreditFacilityRepaymentDashboardItem,
  CreditFacilityRepaymentStatus,
  PaginationData
} from '../redux/types'
import {formatDateOnlyLocale, formatDateTimeLocale} from '../utils/dateHelper'
import {formatMoney} from '../utils/moneyHelper'
import {createUrlWithQueryString} from '../utils/url'

type Summary = Partial<Record<CreditFacilityRepaymentStatus, number>>

const statusOptions: Array<{value: CreditFacilityRepaymentStatus | '', label: string}> = [
  {value: '', label: 'Default operational view'},
  {value: 'due', label: 'Due'},
  {value: 'overdue', label: 'Overdue'},
  {value: 'excluded', label: 'Excluded'},
  {value: 'completed', label: 'Completed'},
  {value: 'configurationError', label: 'Configuration error'},
  {value: 'consistencyError', label: 'Consistency error'},
  {value: 'upcoming', label: 'Upcoming'},
]

const getDateInputValue = (daysFromToday: number) => {
  const date = new Date()
  date.setDate(date.getDate() + daysFromToday)
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  return `${year}-${month}-${day}`
}

const CreditFacilityRepayments = () => {
  const styles = useStyles()
  const fetchClient = useFetchClient()
  const fetchClientRef = useRef(fetchClient)
  fetchClientRef.current = fetchClient
  const [items, setItems] = useState<CreditFacilityRepaymentDashboardItem[]>([])
  const [summary, setSummary] = useState<Summary>({})
  const [pagination, setPagination] = useState<PaginationData>({
    pageIndex: 1,
    pageSize: 50,
    totalItemCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  })
  const [pageIndex, setPageIndex] = useState(1)
  const [status, setStatus] = useState<CreditFacilityRepaymentStatus | ''>('')
  const [queryInput, setQueryInput] = useState('')
  const [query, setQuery] = useState('')
  const [fromDate, setFromDate] = useState(() => getDateInputValue(-7))
  const [toDate, setToDate] = useState(() => getDateInputValue(7))
  const [autoUpdateInterval, setAutoUpdateInterval] = useState<number | null>(null)
  const [selected, setSelected] = useState<CreditFacilityRepaymentDashboardItem>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const url = createUrlWithQueryString(restConfig.routes.creditFacilityRepayments, {
      status,
      query,
      fromDate,
      toDate,
      pageNumber: pageIndex,
      pageSize: pagination.pageSize,
    })

    try {
      const response = await fetchClientRef.current.get(url)
      setItems(response.data as CreditFacilityRepaymentDashboardItem[])
      const paginationHeader = response.raw.headers.get('x-Pagination')
      if (paginationHeader) setPagination(JSON.parse(paginationHeader) as PaginationData)
      const summaryHeader = response.raw.headers.get('x-Repayment-Summary')
      if (summaryHeader) setSummary(normalizeSummary(JSON.parse(summaryHeader)))
    } catch (requestError) {
      setError(String(requestError))
    } finally {
      setLoading(false)
    }
  }, [status, query, fromDate, toDate, pageIndex, pagination.pageSize])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!autoUpdateInterval) return
    const interval = window.setInterval(load, autoUpdateInterval * 1000)
    return () => window.clearInterval(interval)
  }, [autoUpdateInterval, load])

  const applySearch = (event: FormEvent) => {
    event.preventDefault()
    setPageIndex(1)
    setQuery(queryInput.trim())
  }

  const setInclusion = async (item: CreditFacilityRepaymentDashboardItem, include: boolean) => {
    const action = include ? 'include' : 'ignore'
    if (!window.confirm(`${include ? 'Include' : 'Ignore'} this payout when matching bank transactions?`)) return
    try {
      await fetchClientRef.current.post(`${restConfig.routes.creditFacilityRepayments}/${item.creditFacilityPayoutId}/${action}`)
      setSelected(undefined)
      await load()
    } catch (requestError) {
      setError(String(requestError))
    }
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1>Credit facility repayments</h1>
          <p className={styles.subtitle}>Incoming credit facility repayments.</p>
        </div>
        <Toolbar label="Auto update">
          <ToolbarItem icon={faStop} title="Off" display="iconAndText"
                       disabled={!autoUpdateInterval} onClick={() => setAutoUpdateInterval(null)}/>
          <ToolbarItem icon={faRetweet} title="5s" display="iconAndText"
                       disabled={autoUpdateInterval === 5} onClick={() => setAutoUpdateInterval(5)}/>
          <ToolbarItem icon={faRetweet} title="1m" display="iconAndText"
                       disabled={autoUpdateInterval === 60} onClick={() => setAutoUpdateInterval(60)}/>
        </Toolbar>
      </div>

      <CardSection>
        <SummaryCard label="Due" value={summary.due ?? 0}/>
        <SummaryCard label="Overdue" value={summary.overdue ?? 0}/>
        <SummaryCard label="Excluded" value={summary.excluded ?? 0}/>
        <SummaryCard label="Configuration errors" value={summary.configurationError ?? 0}/>
        <SummaryCard label="Consistency errors" value={summary.consistencyError ?? 0}/>
      </CardSection>

      <Card className={`${styles.listCard} card`}>
        <Form className={styles.filters} onSubmit={applySearch}>
          <Form.Control value={queryInput} placeholder="Customer, reference, payout or bank reference"
                        onChange={event => setQueryInput(event.target.value)}/>
          <Form.Select value={status} onChange={event => {
            setPageIndex(1)
            setStatus(event.target.value as CreditFacilityRepaymentStatus | '')
          }}>
            {statusOptions.map(option =>
              <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
          </Form.Select>
          <Form.Control type="date" value={fromDate} onChange={event => {
            setPageIndex(1)
            setFromDate(event.target.value)
          }}/>
          <Form.Control type="date" value={toDate} onChange={event => {
            setPageIndex(1)
            setToDate(event.target.value)
          }}/>
          <Button type="submit">Search</Button>
        </Form>

        {error && <Alert variant="danger">{error}</Alert>}
        {loading && <div className={styles.loading}><Spinner animation="border"/></div>}

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
            <tr>
              <th>Customer</th><th>Reference</th><th>Repayment date</th><th>Amount</th>
              <th>Status</th>
            </tr>
            </thead>
            <tbody>
            {items.map(item =>
              <tr key={item.creditFacilityPayoutTransactionId} onClick={() => setSelected(item)}>
                <td>{item.customerName}<small>{item.companyRegistrationNumber}</small></td>
                <td>{item.customerReferencePayoutId}</td>
                <td>{formatDateOnlyLocale(item.repaymentDate)}{item.overdueDays > 0 && <small>{item.overdueDays} days overdue</small>}</td>
                <td>{formatMoney(item.amount)}</td>
                <td><Status value={item.status}/></td>
              </tr>)}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <Pagination pageIndex={pageIndex} itemCount={pagination.totalItemCount}
                      pageSize={pagination.pageSize} siblingCount={1} onPageChange={setPageIndex}/>
        </div>
      </Card>

      <RepaymentDetails item={selected} onClose={() => setSelected(undefined)} onSetInclusion={setInclusion}/>
    </div>
  )
}

const SummaryCard = ({label, value}: {label: string, value: number}) => {
  const styles = useStyles()
  return <Card className={`${styles.summaryCard} card`}><span>{label}</span><strong>{value}</strong></Card>
}

const Status = ({value}: {value: string}) => {
  const styles = useStyles()
  return <span className={`${styles.status} ${styles[value as keyof typeof styles] ?? ''}`}>{humanize(value)}</span>
}

const RepaymentDetails = ({item, onClose, onSetInclusion}: {
  item?: CreditFacilityRepaymentDashboardItem
  onClose: () => void
  onSetInclusion: (item: CreditFacilityRepaymentDashboardItem, include: boolean) => Promise<void>
}) => (
  <Modal show={!!item} onHide={onClose} size="lg" centered>
    <Modal.Header closeButton><Modal.Title>Repayment details</Modal.Title></Modal.Header>
    <Modal.Body style={{color: Colors.white}}>
      {item && <div>
        <Detail label="Customer" value={item.customerName}/>
        <Detail label="Customer reference payout ID" value={item.customerReferencePayoutId}/>
        <Detail label="Payout transaction" value={item.creditFacilityPayoutTransactionId}/>
        <Detail label="Repayment transaction" value={item.repaymentTransactionId}/>
        <Detail label="Matched bank transaction" value={item.matchedBankAccountTransactionId}/>
        <Detail label="Bank reference" value={item.matchedBankAccountTransactionReference}/>
        <Detail label="Bank booking date" value={formatDateTimeLocale(item.matchedBankAccountTransactionDate)}/>
        <Detail label="Repaid date" value={formatDateTimeLocale(item.repaidDate)}/>
        <div style={{marginTop: 15}}>
          <Link to={`/payoutcreditfacilities/${item.payoutCreditFacilityId}/payouts/${item.creditFacilityPayoutId}`}>
            Open payout details
          </Link>
        </div>
      </div>}
    </Modal.Body>
    <Modal.Footer>
      {item && item.status !== 'completed' && item.status !== 'consistencyError' &&
        <Button variant={item.ignoreWhenMatchingBankAccountTransactions ? 'success' : 'warning'}
                onClick={() => onSetInclusion(item, item.ignoreWhenMatchingBankAccountTransactions)}>
          {item.ignoreWhenMatchingBankAccountTransactions ? 'Include when matching' : 'Ignore when matching'}
        </Button>}
      <Button variant="secondary" onClick={onClose}>Close</Button>
    </Modal.Footer>
  </Modal>
)

const Detail = ({label, value}: {label: string, value?: string}) =>
  <div><strong>{label}:</strong> {value || '-'}</div>

const humanize = (value: string) => value
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^./, first => first.toUpperCase())

const normalizeSummary = (value: Record<string, number>): Summary => {
  const summary: Summary = {}
  Object.entries(value).forEach(([key, count]) => {
    const normalized = key.charAt(0).toLowerCase() + key.slice(1) as CreditFacilityRepaymentStatus
    summary[normalized] = count
  })
  return summary
}

export default CreditFacilityRepayments

const useStyles = createUseStyles({
  pageHeader: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 25},
  subtitle: {color: Colors.gray3, margin: 0},
  summaryCard: {padding: 18, minWidth: 150, display: 'flex', flexDirection: 'column', backgroundColor: Colors.darkGrayCard,
    '& strong': {fontSize: 28}},
  listCard: {padding: 20, marginTop: 20, backgroundColor: Colors.darkGrayCard},
  filters: {display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 10, marginBottom: 15},
  loading: {display: 'flex', justifyContent: 'center', padding: 20},
  tableContainer: {overflowX: 'auto'},
  table: {width: '100%', borderCollapse: 'collapse', fontSize: 14,
    '& th': {textAlign: 'left', padding: '10px 8px', borderBottom: `1px solid ${Colors.grayBorder}`},
    '& td': {padding: '9px 8px', borderBottom: `1px solid ${Colors.grayBorder}`},
    '& tbody tr': {cursor: 'pointer'},
    '& tbody tr:hover': {backgroundColor: Colors.darkGrayHover},
    '& small': {display: 'block', color: Colors.gray3}},
  pagination: {display: 'flex', justifyContent: 'center', marginTop: 15},
  status: {display: 'inline-block', padding: '2px 8px', borderRadius: 10, backgroundColor: Colors.darkGray3, whiteSpace: 'nowrap'},
  overdue: {backgroundColor: '#7a2828'},
  consistencyError: {backgroundColor: '#7a2828'},
  configurationError: {backgroundColor: '#735c17'},
  due: {backgroundColor: '#355b72'},
  completed: {backgroundColor: '#315f39'},
  pending: {backgroundColor: '#735c17'},
  excluded: {backgroundColor: Colors.gray3},
  notCreated: {backgroundColor: Colors.gray3},
})
